import React from "react";
import {
    ContentSection,
    Grid,
    GridColumn,
    GridRow,
    LabeledDate,
    LabeledText,
    ListTable,
    createListTableColumns,
    ensure,
    renderOptionalTextWithTooltip
} from "sprint-common-web";

import { RecordType } from "../../../../common/types/RecordType";
import { renderRecordType } from "../../../../common/utils/renderRecordType";
import { useGetOnPremOrderDashboard } from "../hooks/useGetOnPremOrderDashboard";
import { OnPremOrderDashboardItem } from "../OnPremOrderDashboardItem";

interface OnPremOrderDeliverySectionProps {
    minHeight: number;
}

export const OnPremOrderDeliverySection: React.VFC<OnPremOrderDeliverySectionProps> = ({
    minHeight
}: OnPremOrderDeliverySectionProps) => {
    const { isLoading, onPremOrderDetails } = useGetOnPremOrderDashboard();

    const columns = createListTableColumns<OnPremOrderFileNamesItem>([
        {
            type: "basic",
            key: "type",
            name: "Type",
            minWidth: 38,
            maxWidth: 50,
            onRender: renderRecordType
        },
        {
            type: "basic",
            key: "targetFilename",
            name: "Target filename",
            minWidth: 38,
            maxWidth: 150,
            onRender: renderOptionalTextWithTooltip
        }
    ]);

    return (
        <ContentSection
            header="Delivery"
            style={{ minHeight, flex: 1 }}
            isLoading={isLoading}
            testId={"onprem-order-delivery-section"}
        >
            <Grid>
                <GridRow>
                    <GridColumn lg={4}>
                        <LabeledDate
                            label="Due date"
                            testId="onprem-order-dueDate"
                            value={new Date(onPremOrderDetails.dueDate)}
                        />
                    </GridColumn>
                    <GridColumn lg={4}>
                        <LabeledText
                            label="Delivery to"
                            testId="onprem-order-deliveryTo"
                            value={onPremOrderDetails.deliveryTo}
                        />
                    </GridColumn>
                    <GridColumn lg={4}>
                        <LabeledText
                            label="Delivery details"
                            testId="onprem-order-deliveryDetails"
                            value={onPremOrderDetails.deliveryDetails}
                        />
                    </GridColumn>
                </GridRow>
                <GridRow>
                    <GridColumn lg={12}>
                        <ListTable
                            columns={columns}
                            items={mapToOnPremOrderTargetFileNamesItem(onPremOrderDetails)}
                            isLoading={isLoading}
                            shimmerLines={1}
                            noItemsMessage="No filenames"
                            onRenderRow={(props, defaultRender) => {
                                ensure(props);
                                ensure(defaultRender);

                                return defaultRender({ ...props, styles: { root: { border: "none" } } });
                            }}
                        />
                    </GridColumn>
                </GridRow>
            </Grid>
        </ContentSection>
    );
};

interface OnPremOrderFileNamesItem {
    type: RecordType;
    targetFilename?: string;
}

const mapToOnPremOrderTargetFileNamesItem = (
    orderDashboardItem: OnPremOrderDashboardItem
): OnPremOrderFileNamesItem[] => {
    return [
        {
            type: RecordType.Video,
            targetFilename: orderDashboardItem.onPremAssets.video.targetFilename
        },
        ...orderDashboardItem.onPremAssets.subtitles
            .filter((s) => s.targetFilename)
            .map((subtitle) => ({
                type: RecordType.Subtitle,
                targetFilename: subtitle.targetFilename
            }))
    ];
};
